import {Log} from '@aboutyou/mlm';
import {useQueryParamsCallback} from '@aboutyou/router';
import {useIsomorphicLayoutEffect} from '@aboutyou/ui-components';
import React, {useCallback, useEffect, useRef} from 'react';
import type {CheckoutEntryDetails} from '../hooks/useCheckoutEntryDetails';
import {useScaylePaymentMethodState} from '../scayle-state/useScaylePaymentMethodState';
import {useScayleState} from '../scayle-state/useScayleState';
import {CHECKOUT_TRACKING_EVENT} from './CheckoutTrackingListener';
import {
  CheckoutTrackingEvent,
  buildFallbackInitEvents,
  mapCheckoutTrackingEvent,
} from './mapCheckoutTrackingEvent';
import {useBnplStatusTracking} from './useBnplStatusTracking';

type CheckoutDataLayerInterceptorProps = {
  device: string;
  gaSessionId?: string;
  checkoutEntryDetails?: CheckoutEntryDetails;
};

export const CheckoutDataLayerInterceptor: React.FC<
  CheckoutDataLayerInterceptorProps
> = ({device, gaSessionId, checkoutEntryDetails}) => {
  const bufferRef = useRef<CheckoutTrackingEvent[]>([]);
  const hasCOInitTracked = useRef(false);

  const getQueryParams = useQueryParamsCallback();
  const scayleState = useScayleState().data;
  const {selectedPaymentOption, selectedShippingOption, bnplOption} =
    useScaylePaymentMethodState();

  useBnplStatusTracking();

  const pushFallbackInitEvents = useCallback(() => {
    const search = getQueryParams();
    const events = buildFallbackInitEvents({
      device,
      search,
      gaSessionId,
      checkoutEntryDetails: checkoutEntryDetails ?? null,
    });
    window.dataLayer.push(...events);
  }, [checkoutEntryDetails, device, gaSessionId, getQueryParams]);

  const pushToDataLayer = useCallback(
    async (checkoutDataLayerEvent: CheckoutTrackingEvent) => {
      const search = getQueryParams();

      const processed = mapCheckoutTrackingEvent({
        checkoutDataLayerEvent,
        device,
        search,
        scayleState,
        gaSessionId,
        checkoutEntryDetails: checkoutEntryDetails ?? null,
        selectedPaymentOption: selectedPaymentOption ?? null,
        selectedShippingOption: selectedShippingOption ?? null,
        bnplOption: bnplOption ?? null,
      });

      if (!processed) {
        // eslint-disable-next-line no-console
        console.log(`>> Rejected CO tracking event: `, checkoutDataLayerEvent);
        return;
      }

      if (Array.isArray(processed)) {
        window.dataLayer.push(...processed);
      } else {
        window.dataLayer.push(processed);
      }
    },
    [
      bnplOption,
      checkoutEntryDetails,
      device,
      gaSessionId,
      getQueryParams,
      scayleState,
      selectedPaymentOption,
      selectedShippingOption,
    ],
  );

  useIsomorphicLayoutEffect(() => {
    window.dataLayer = window.dataLayer || [];

    const handleTrackingEvent = (event: Event) => {
      const item = (event as CustomEvent<CheckoutTrackingEvent>).detail;

      if (item.event === 'shop_init') {
        hasCOInitTracked.current = true;
      }

      // In case of empty basket error, we might not get shop_init event, so we push fallback init events to make sure the error is actionable in the data layer.
      if (
        item.event === 'content_view' &&
        item.page_type.includes('error_basket_empty')
      ) {
        if (!hasCOInitTracked.current) {
          pushFallbackInitEvents();

          hasCOInitTracked.current = true; // Mark shop_init as tracked to avoid pushing fallback init events again for other events.
        }

        pushToDataLayer(item).catch(LogTrackingError);
        return;
      }

      // Buffer experiment_activation events until shop_init has been tracked, as tracking Analytics experiments requires shop_init to be present in the data layer.
      if (item.event === 'experiment_activation' && !hasCOInitTracked.current) {
        bufferRef.current.push(item);
        return;
      }

      if (
        item.event === 'feature' &&
        item.label === 'payment_methods_loading_time' &&
        !selectedPaymentOption
      ) {
        bufferRef.current.push(item);
        return;
      }

      // INVESTIGATION: We saw some cases where we get other CO events before shop_init. This is unexpected and should not happen
      // if we get any events (other than error) before shop_init, we do a DD log for investigation.
      // NOTE: This log can be deleted after we have investigated the issue and are confident that it is not happening anymore.

      // This is an in-between state where the user has completed the checkout but the payment is still pending.
      // In this case, we might get a content_view event before shop_init, which is expected and should not be logged as an error.
      // example: https://www.aboutyou.de/checkout?waitForPaymentConfirmation=true
      const isExpectedContentViewEvent =
        item.event === 'content_view' &&
        item.page_type === 'checkout:payment_confirmation_pending';

      if (
        item.event !== 'shop_init' &&
        item.event !== 'error' &&
        !isExpectedContentViewEvent &&
        !hasCOInitTracked.current
      ) {
        Log.unSampledLog('UnexpectedCOEventBeforeShopInit', {
          datalayer_event: item.event,
          item,
        });
      }

      // Error arrived before shop_init was tracked — emit synthetic init
      // (co_init/client_signals) first so init precedes the error in the data
      // layer.
      if (item.event === 'error' && !hasCOInitTracked.current) {
        pushFallbackInitEvents();
        hasCOInitTracked.current = true; // Mark shop_init as tracked to avoid pushing fallback init events again for other events.

        pushToDataLayer(item).catch(LogTrackingError);
        return;
      }

      if (scayleState) {
        pushToDataLayer(item).catch(LogTrackingError);
      } else {
        bufferRef.current.push(item);
      }
    };

    window.addEventListener(CHECKOUT_TRACKING_EVENT, handleTrackingEvent);

    return () => {
      window.removeEventListener(CHECKOUT_TRACKING_EVENT, handleTrackingEvent);
    };
  }, [
    pushFallbackInitEvents,
    pushToDataLayer,
    scayleState,
    selectedPaymentOption,
  ]);

  useEffect(() => {
    // Flush the buffer once scayleState is available and if there are any buffered events
    if (bufferRef.current.length > 0) {
      for (const raw of bufferRef.current) {
        pushToDataLayer(raw).catch(LogTrackingError);
      }
      bufferRef.current.length = 0;
    }
  });

  return null;
};

const LogTrackingError = (error: Error) => {
  Log.error(
    '@aboutyou/logic-components',
    'Error pushing checkout tracking event to data layer',
    {error},
  );
};
